import { Dialog, DialogContent, FormControl, InputLabel, MenuItem, Select, Switch } from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { useDataProvider, useNotify } from "react-admin";
import { Coupon } from "../../types/coupons";
import { CouponViewModal } from "../common/couponsShared/couponsModals/couponModalView";
import { CouponsTable } from "../common/couponsShared/couponsTable/couponsTable";
import { RemoveEntityModalContent } from "../common/removeEntityPopUp/RemoveEntity";

interface State {
  coupons: Array<Coupon>;
  total: number;
  page: number;
  perPage: number;
  field: string;
  order: string;
  searchField: string;
  searchValue: string;
}

interface Props {
  id: string;
  isView: boolean;
}

export const CouponTab = ({ id, isView }: Props) => {
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const [state, setState] = useState<State>({
    coupons: Array<Coupon>(),
    total: 0,
    page: 0,
    perPage: 10,
    field: "_id",
    order: "asc",
    searchField: "couponId",
    searchValue: "",
  });

  const [couponType, setCouponType] = useState<string>("order");
  const [currentCoupon, setCurrentCoupon] = useState<Coupon>();
  const [modalsState, setModals] = useState({
    viewCoupon: false,
    deleteCoupon: false,
  });

  useEffect(() => {
    getCoupons(
      1,
      10,
      state.field,
      state.order,
      state.searchField,
      state.searchValue,
      couponType,
    );
  }, [couponType]);

  const getCoupons = async (
    page: number,
    perPage: number,
    field: string,
    order: string,
    searchField: string,
    searchValue: string,
    couponType: string,
  ): Promise<void> => {
    const result = (await dataProvider.getUserCoupons(`coupons/user/${id}`, {
      sort: { field, order },
      search: { field: searchField, value: searchValue },
      filter: {},
      pagination: { page, perPage },
      couponType: couponType,
    })) as any;
    setState({
      ...state,
      coupons: result.data.coupons,
      total: result.data.allCount,
      page,
      perPage,
      field,
      order,
      searchField,
      searchValue,
    });
  };

  const handleFirstPage = () => {
    getCoupons(
      1,
      state.perPage,
      state.field,
      state.order,
      state.searchField,
      state.searchValue,
      couponType,
    );
  };

  const handleLastPage = () => {
    const page = Math.ceil(state.total / state.perPage);
    getCoupons(
      page,
      state.perPage,
      state.field,
      state.order,
      state.searchField,
      state.searchValue,
      couponType,
    );
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ): void => {
    getCoupons(
      newPage + 1,
      state.perPage,
      state.field,
      state.order,
      state.searchField,
      state.searchValue,
      couponType,
    );
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const perPage = parseInt(event.target.value, 10);
    getCoupons(
      1,
      perPage,
      state.field,
      state.order,
      state.searchField,
      state.searchValue,
      couponType,
    );
  };

  const handleCloseModals =
    (modalName: "viewCoupon" | "deleteCoupon") => () => {
      setModals((state) => ({
        ...state,
        [modalName]: false,
      }));
    };

  const handleShowModals = (modalName: "viewCoupon" | "deleteCoupon") => {
    if (modalName === "viewCoupon" || modalName === "deleteCoupon") {
      return (couponId: string | undefined) => {
        const coupon = state.coupons.find((i) => i._id === couponId);
        setCurrentCoupon(coupon);
        setModals((state) => ({
          ...state,
          [modalName]: true,
        }));
      };
    }
    return () => {
      setModals((state) => ({
        ...state,
        [modalName]: true,
      }));
    };
  };

  const handleDeleteCoupon = (couponId: string) => async () => {
    try {
      const result = await dataProvider.delete("coupons", {
        id: couponId,
        previousData: {} as any,
      });
      if (result.data) {
        notify(result.data.message, "info");
        getCoupons(
          state.page,
          state.perPage,
          state.field,
          state.order,
          state.searchField,
          state.searchValue,
          couponType,
        );
        setModals({
          ...modalsState,
          deleteCoupon: false,
        });
      }
    } catch (error) {
      notify("Coupon not found", "error");
    }
  };

  const handleSort = (direction: string, field: string) => {
    getCoupons(
      state.page,
      state.perPage,
      field,
      direction,
      state.searchField,
      state.searchValue,
      couponType,
    );
  };

  const applySearch = (field: string) => {
    getCoupons(
      1,
      state.perPage,
      state.field,
      state.order,
      field,
      state.searchValue,
      couponType,
    );
  };

  const search = (value: string, field: string) => {
    setState({ ...state, searchField: field, searchValue: value });
  };

  const clearSearch = (field: string) => {
    setState({ ...state, searchValue: "" });
  };

  const handleChangeType = (event: React.ChangeEvent<{ value: unknown }>) => {
    setCouponType(event.target.value as string);
  }

  return (
    <div>
      <FormControl variant="outlined" className="coupon-type-select">
        <Select
          labelId="demo-simple-select-label"
          id="coupon-type"
          value={couponType}
          onChange={handleChangeType}
        >
          <MenuItem value={"order"}>Single Order Coupons</MenuItem>
          <MenuItem value={"subscription"}>Subscription Coupons</MenuItem>
        </Select>
      </FormControl>
      <CouponsTable
        coupons={state.coupons}
        tableComponentScope={"userMenengment"}
        page={state.page}
        perPage={state.perPage}
        collectionLength={state.coupons.length}
        total={state.total}
        isView={isView}
        handleShowViewModal={handleShowModals("viewCoupon")}
        handleShowDeleteModal={handleShowModals("deleteCoupon")}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        handleFirstPage={handleFirstPage}
        handleLastPage={handleLastPage}
        handleSort={handleSort}
        applySearch={applySearch}
        search={search}
        clearSearch={clearSearch}
        searchValue={state.searchValue}
      />
      {modalsState.viewCoupon && currentCoupon && (
        <CouponViewModal
          coupon={currentCoupon}
          closeModal={handleCloseModals("viewCoupon")}
          showModal={modalsState.viewCoupon}
          couponStatusType={"server"}
        />
      )}
      {modalsState.deleteCoupon && currentCoupon && (
        <Dialog
          fullWidth
          open={modalsState.deleteCoupon}
          onClose={handleCloseModals("deleteCoupon")}
        >
          <DialogContent>
            <RemoveEntityModalContent
              closeModal={handleCloseModals("deleteCoupon")}
              removeUser={handleDeleteCoupon(currentCoupon?._id!)}
              entityName="coupon"
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
    // <CouponsTable/>
  );
};
